/*
 * Invoices and work items (owner, 2026-10-21): each invoice line may point at
 * one work item of the invoice's client(s). Lines made from tracked time are
 * linked automatically; other lines are linked by picking a work item.
 */
import { WorkModel } from "../../../../factories/works/models/work.model";
import { InvoiceFormItem } from "./invoice-form.types";

export type WorkOption = { id: string; label: string };

type WorkRow = Pick<
  WorkModel,
  "workId" | "workName" | "customerId" | "customerName" | "jobStatus"
>;

/** "Name (Status)", with the client first when the invoice has several clients. */
export const workOptionLabel = (work: WorkRow, showClient: boolean) => {
  const name = (work.workName || "").trim() || "Untitled work";
  const client =
    showClient && work.customerName ? `${work.customerName} · ` : "";
  const status = work.jobStatus ? ` (${work.jobStatus})` : "";
  return `${client}${name}${status}`;
};

/**
 * Work item choices for one line: the clients' work (as loaded, no
 * duplicates), plus the line's own link when it isn't in the list (e.g. the
 * work was archived since), so an existing link is never lost by opening Edit.
 */
export const workOptionsFor = (
  works: WorkRow[],
  line?: Pick<InvoiceFormItem, "workId" | "workName">,
): WorkOption[] => {
  const showClient = new Set(works.map((w) => w.customerId)).size > 1;
  const seen = new Set<string>();
  const options: WorkOption[] = [];
  works.forEach((w) => {
    if (!w.workId || seen.has(w.workId)) return;
    seen.add(w.workId);
    options.push({ id: w.workId, label: workOptionLabel(w, showClient) });
  });
  if (line?.workId && !seen.has(line.workId))
    options.unshift({
      id: line.workId,
      label: line.workName?.trim() || "Linked work item",
    });
  return options;
};

/** Link (or unlink with "") a typed line to a work item. */
export const linkLineToWork = (
  workId: string,
  works: Pick<WorkModel, "workId" | "workName" | "workTypeId">[],
): Pick<InvoiceFormItem, "workId" | "workName" | "workTypeId"> => {
  if (!workId) return { workId: null, workName: undefined, workTypeId: null };
  const work = works.find((w) => w.workId === workId);
  return {
    workId,
    workName: work?.workName,
    workTypeId: work?.workTypeId || null,
  };
};

/** Seconds -> "3h 20m" (or "0h"). */
export const hoursLabel = (seconds: number) => {
  const minutes = Math.round((seconds || 0) / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h && !m) return "0h";
  return m ? `${h}h ${m}m` : `${h}h`;
};
